import type {
  TrainingAnswer,
  TrainingAnswers,
  TrainingFollowUpDef,
  TrainingOption,
  TrainingPageDef,
  TrainingPlayerPage,
} from './types';

/**
 * Follow-up pages: which earlier questions a learner must expand on.
 *
 * Pure and shared by the player (to render the boxes and gate "Next"), the
 * builder preview, and the server's answers report, so all three agree on
 * exactly which questions matched. Works over authoring pages and player
 * pages alike: the choice types carry their options in both shapes.
 */

/** One earlier question whose chosen answer matched the follow-up label. */
export interface FollowUpTarget {
  /** The source question (or grid row) id; keys the explanation in the map. */
  question_id: string;
  prompt: string;
  /** The option label the learner chose, e.g. "Yes". */
  answer_label: string;
}

/** Page types a follow-up can look back over. */
export const FOLLOW_UP_SOURCE_TYPES = [
  'single_choice',
  'multi_select',
  'true_false',
  'question_group',
] as const;

export function isFollowUpSourceType(type: string): boolean {
  return (FOLLOW_UP_SOURCE_TYPES as readonly string[]).includes(type);
}

/** Label comparison used by the lookup: trimmed and case-insensitive. */
export function normaliseFollowUpLabel(label: string): string {
  return label.trim().toLowerCase();
}

function chosenLabel(options: TrainingOption[], answer: TrainingAnswer | undefined): string[] {
  if (typeof answer === 'string') {
    const o = options.find((opt) => opt.id === answer);
    return o ? [o.label] : [];
  }
  if (Array.isArray(answer)) {
    return answer
      .map((id) => options.find((opt) => opt.id === id)?.label)
      .filter((l): l is string => l !== undefined);
  }
  return [];
}

/**
 * The questions this follow-up page should ask about, in module order,
 * given the answers so far. Only pages BEFORE the follow-up are considered
 * (a later page cannot have been answered yet when it is reached), further
 * narrowed to `source_page_ids` when the author set any.
 */
export function followUpTargets(
  pages: ReadonlyArray<TrainingPageDef | TrainingPlayerPage>,
  answers: TrainingAnswers,
  page: TrainingFollowUpDef,
): FollowUpTarget[] {
  const wanted = normaliseFollowUpLabel(page.match_label);
  if (wanted === '') return [];
  const at = pages.findIndex((p) => p.id === page.id);
  const before = at === -1 ? pages : pages.slice(0, at);
  const only =
    page.source_page_ids && page.source_page_ids.length > 0 ? new Set(page.source_page_ids) : null;
  const out: FollowUpTarget[] = [];
  const matches = (label: string) => normaliseFollowUpLabel(label) === wanted;

  for (const p of before) {
    if (only && !only.has(p.id)) continue;
    switch (p.type) {
      case 'single_choice':
      case 'multi_select': {
        const hit = chosenLabel(p.options, answers[p.id]).find(matches);
        if (hit !== undefined) {
          out.push({ question_id: p.id, prompt: p.prompt, answer_label: hit });
        }
        break;
      }
      case 'true_false': {
        const a = answers[p.id];
        if (typeof a !== 'boolean') break;
        const label = a ? 'True' : 'False';
        if (matches(label)) out.push({ question_id: p.id, prompt: p.prompt, answer_label: label });
        break;
      }
      case 'question_group': {
        for (const row of p.rows) {
          const hit = chosenLabel(p.options, answers[row.id]).find(matches);
          if (hit !== undefined) {
            out.push({ question_id: row.id, prompt: row.prompt, answer_label: hit });
          }
        }
        break;
      }
      default:
        break;
    }
  }
  return out;
}

/** The stored explanations for a follow-up page, or an empty map. */
export function followUpAnswerMap(answer: TrainingAnswer | undefined): Record<string, string> {
  if (typeof answer !== 'object' || answer === null || Array.isArray(answer) || 'x' in answer) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(answer)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
}

/**
 * Whether a follow-up page is complete: every matched question has some
 * text, unless the page is optional. An empty match list is complete.
 */
export function followUpComplete(
  pages: ReadonlyArray<TrainingPageDef | TrainingPlayerPage>,
  answers: TrainingAnswers,
  page: TrainingFollowUpDef,
): boolean {
  if (page.optional) return true;
  const given = followUpAnswerMap(answers[page.id]);
  return followUpTargets(pages, answers, page).every(
    (t) => typeof given[t.question_id] === 'string' && given[t.question_id]!.trim() !== '',
  );
}
